/**
 * Unified analytics helper for GA4 ecommerce + Microsoft Clarity custom tags.
 * Safe to call from anywhere — no-ops if scripts haven't loaded.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";
const hasClarity = () => typeof window !== "undefined" && typeof window.clarity === "function";

export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  item_category?: string; // 'book' | 'lesson'
  item_brand?: string;
  price: number;
  quantity?: number;
}

export const ga = (event: string, params: Record<string, unknown> = {}) => {
  if (!hasGtag()) return;
  try {
    window.gtag!("event", event, params);
  } catch (e) {
    console.warn("[analytics] gtag failed", e);
  }
};

export const clarityTag = (key: string, value: string) => {
  if (!hasClarity()) return;
  try {
    window.clarity!("set", key, value);
  } catch (e) {
    console.warn("[analytics] clarity failed", e);
  }
};

export const trackPageView = (path: string, title?: string) => {
  if (hasGtag()) {
    window.gtag!("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  }
};

// ---------- Ecommerce funnel ----------

export const trackViewItem = (item: AnalyticsItem) => {
  ga("view_item", {
    currency: "EUR",
    value: item.price,
    items: [{ ...item, quantity: item.quantity ?? 1 }],
  });
};

export const trackAddToCart = (item: AnalyticsItem) => {
  ga("add_to_cart", {
    currency: "EUR",
    value: item.price * (item.quantity ?? 1),
    items: [{ ...item, quantity: item.quantity ?? 1 }],
  });
  clarityTag("cart_action", "add");
};

export const trackRemoveFromCart = (item: AnalyticsItem) => {
  ga("remove_from_cart", {
    currency: "EUR",
    value: item.price * (item.quantity ?? 1),
    items: [{ ...item, quantity: item.quantity ?? 1 }],
  });
};

export const trackViewCart = (items: AnalyticsItem[], total: number) => {
  ga("view_cart", {
    currency: "EUR",
    value: total,
    items,
  });
};

export const trackBeginCheckout = (
  items: AnalyticsItem[],
  total: number,
  discountCode?: string | null,
) => {
  ga("begin_checkout", {
    currency: "EUR",
    value: total,
    coupon: discountCode || undefined,
    items,
  });
  clarityTag("funnel_step", "begin_checkout");
};

export const trackAddPaymentInfo = (method: "stripe" | "paysera", total: number) => {
  ga("add_payment_info", {
    currency: "EUR",
    value: total,
    payment_type: method,
  });
  clarityTag("payment_method", method);
};

export const trackPurchase = (params: {
  transactionId: string;
  value: number;
  items: AnalyticsItem[];
  coupon?: string | null;
  paymentMethod?: string;
}) => {
  ga("purchase", {
    transaction_id: params.transactionId,
    value: params.value,
    currency: "EUR",
    coupon: params.coupon || undefined,
    items: params.items,
  });
  clarityTag("converted", "true");
  if (params.paymentMethod) clarityTag("payment_method", params.paymentMethod);
};

export const trackPurchaseCanceled = (reason: "user_canceled" | "failed") => {
  ga("checkout_canceled", { reason });
  clarityTag("checkout_outcome", reason);
};

// ---------- Helpers to build items from cart ----------

export const cartItemToAnalytics = (
  product: { id: string; title: string; type: string; price: number; author?: string | null },
  quantity = 1,
): AnalyticsItem => ({
  item_id: product.id,
  item_name: product.title,
  item_category: product.type,
  item_brand: product.author || "Diet of Happiness",
  price: product.price,
  quantity,
});
